/* eslint-disable camelcase */
import '@/publicPath';
import { apm } from '@elastic/apm-rum';
import Cookies from 'js-cookie';
import { storeToRefs } from 'pinia';
import type { RouteLocationNormalized, RouteLocationRaw, RouterOptions } from 'vue-router';
import { NavigationFailureType, createRouter, createWebHistory, isNavigationFailure } from 'vue-router';
import { useFeatureToggleStore } from './stores/featureToggleStore';
import { useHeaderDisplayStore } from './stores/headerDisplayStore';
import { useHistoryStore } from './stores/historyStore';
import { useRootStore } from './stores/rootStore';
import { DisplayMode, MarketPage, RoutePage, SportType } from '@/interface/enum';
import { recordRecentRoute } from '@/libraries/helpers/missingHttpRefererHelper';
import { getCurrentMultipleSportsZone, getMultipleSportsZonePathPrefix } from '@/libraries/helpers/multipleSportsZoneHelper';
import { getOrderedSimulationSportList } from '@/libraries/helpers/sportsHelper';
import { setLangFromCookie } from '@/libraries/i18n';
import { getHost, getSportsPromotionUrl } from '@/libraries/url';
import { useAccountStore } from '@/stores/accountStore';
import { useProductPlatformStore } from '@/stores/productPlatformStore';
import ErrorPage from '@/views/ErrorPage.vue';
import Main from '@/views/Main.vue';

const MyBets = () => import('@/views/MyBets.vue');
const CashOut = () => import('@/views/CashOut.vue');
const Results = () => import('@/views/Results.vue');
const MatchDetailView = () => import('@/views/MatchDetailView.vue');
const MyMatches = () => import('@/views/MyMatches.vue');
const Exchange = () => import('@/views/Exchange.vue');
const ExchangeBettingPage = () => import('@/components/exchange/BettingPage.vue');
const ExchangeTransfer = () => import('@/components/exchange/Transfer.vue');
const Jackpot = () => import('@/views/Jackpot.vue');
const TournamentCenter = () => import('@/views/TournamentCenter.vue');
const TournamentCenterOverview = () => import('@/components/tournamentCenter/TournamentCenterOverview.vue');
const TournamentCenterCountries = () => import('@/components/tournamentCenter/TournamentCenterCountries.vue');
const TournamentCenterMatches = () => import('@/components/tournamentCenter/TournamentCenterMatches.vue');
const TournamentCenterPromotion = () => import('@/components/tournamentCenter/TournamentCenterPromotion.vue');

const baseUrl = '';

export const multipleSportsPathPrefix = getMultipleSportsZonePathPrefix(getCurrentMultipleSportsZone());

const scrollBehavior: RouterOptions['scrollBehavior'] = (to, from, savedPosition) => {
    const { isComponentInPp } = storeToRefs(useProductPlatformStore());

    if (isComponentInPp.value) {
        return Promise.resolve(undefined);
    }

    if (savedPosition) {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(savedPosition);
            }, 500);
        });
    }
    return Promise.resolve({ left: 0, top: 0 });
};

export const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/my-bets',
            name: RoutePage.MyBets,
            component: MyBets,
            meta: { isAsiOnly: true },
        },
        {
            path: '/cash-out',
            name: RoutePage.CashOut,
            component: CashOut,
            meta: { isAsiOnly: true },
        },
        {
            path: '/results',
            name: RoutePage.Results,
            component: Results,
            meta: { isAsiOnly: true },
        },
        {
            path: '/my-matches',
            name: RoutePage.MyMatches,
            component: MyMatches,
            meta: { isAsiOnly: true },
        },
        {
            path: '/jackpot',
            name: RoutePage.Jackpot,
            component: Jackpot,
            meta: {
                isEnabled: () => {
                    const { isJackpotEnabled } = storeToRefs(useFeatureToggleStore());
                    return isJackpotEnabled.value;
                },
            },
        },
        {
            path: '/tournament-center',
            component: TournamentCenter,
            meta: {
                isEnabled: () => {
                    const { isTournamentCenterEnabled } = storeToRefs(useFeatureToggleStore());
                    return isTournamentCenterEnabled.value;
                },
            },
            children: [
                {
                    path: '',
                    name: RoutePage.TournamentCenterOverview,
                    component: TournamentCenterOverview,
                    meta: {
                        isEnabled: () => {
                            const { isTournamentCenterEnabled } = storeToRefs(useFeatureToggleStore());
                            return isTournamentCenterEnabled.value;
                        },
                    },
                },
                {
                    path: 'countries',
                    name: RoutePage.TournamentCenterCountries,
                    component: TournamentCenterCountries,
                    meta: {
                        isEnabled: () => {
                            const { isTournamentCenterEnabled } = storeToRefs(useFeatureToggleStore());
                            return isTournamentCenterEnabled.value;
                        },
                    },
                },
                {
                    path: 'matches/:outright(outright)?/:sportRouteName?',
                    name: RoutePage.TournamentCenterMatches,
                    component: TournamentCenterMatches,
                    beforeEnter: (to, from, next) => {
                        if (!to.params.sportRouteName) {
                            router.push({
                                name: RoutePage.TournamentCenterMatches,
                                params: {
                                    sportRouteName: SportType.toRouteName(SportType.Soccer),
                                },
                            });
                            return;
                        }

                        const sportRouteName = to.params.sportRouteName;
                        const sportType = typeof sportRouteName === 'string'
                            ? SportType.fromRouteName(sportRouteName)
                            : undefined;

                        useRootStore().updateSportType(sportType, false);

                        const outright = to.params.outright;
                        const marketPage = typeof outright === 'string' && outright.toLowerCase() === 'outright'
                            ? MarketPage.Outright
                            : MarketPage.All;

                        useRootStore().updateMarketPage(marketPage);

                        next();
                    },
                    meta: {
                        isEnabled: () => {
                            const { isTournamentCenterEnabled } = storeToRefs(useFeatureToggleStore());
                            return isTournamentCenterEnabled.value;
                        },
                    },
                },
                {
                    path: 'promotion',
                    name: RoutePage.TournamentCenterPromotion,
                    component: TournamentCenterPromotion,
                    beforeEnter: (_to, _from, next) => {
                        const { isEuroSite } = storeToRefs(useFeatureToggleStore());
                        if (isEuroSite.value) {
                            globalThis.location.href = getSportsPromotionUrl();
                            next(false);
                            return;
                        }
                        next();
                    },
                    meta: {
                        isEnabled: () => {
                            const { isTournamentCenterEnabled, isPromotionEnabled, isEuroSite } = storeToRefs(useFeatureToggleStore());
                            if (isEuroSite.value) {
                                return isPromotionEnabled.value;
                            }
                            return isTournamentCenterEnabled.value && isPromotionEnabled.value;
                        },
                    },
                },
            ],
        },
        {
            path: '/exchange',
            component: Exchange,
            meta: {
                isEnabled: () => {
                    const { isFairExchangeEnabled } = storeToRefs(useFeatureToggleStore());
                    return isFairExchangeEnabled.value;
                },
            },
            children: [
                {
                    path: '',
                    name: RoutePage.Exchange,
                    component: ExchangeBettingPage,
                    meta: {
                        isEnabled: () => {
                            const { isFairExchangeEnabled } = storeToRefs(useFeatureToggleStore());
                            return isFairExchangeEnabled.value;
                        },
                    },
                },
                {
                    path: 'transfer',
                    name: RoutePage.ExchangeTransfer,
                    component: ExchangeTransfer,
                    meta: {
                        isEnabled: () => {
                            const { isFairExchangeEnabled } = storeToRefs(useFeatureToggleStore());
                            return isFairExchangeEnabled.value;
                        },
                    },
                },
            ],
        },
        {
            path: '/:sportType/:leagueName/:matchName/:matchId',
            name: RoutePage.MatchDetail,
            component: MatchDetailView,
            props: route => ({
                sportTypeRoute: typeof route.params.sportType === 'string'
                    ? SportType.fromRouteName(route.params.sportType)
                    : undefined,
                leagueName: typeof route.params.leagueName === 'string'
                    ? route.params.leagueName.replace(/-/g, ' ')
                    : undefined,
                matchName: typeof route.params.matchName === 'string'
                    ? route.params.matchName.replace(/-/g, ' ')
                    : undefined,
                matchId: Number(route.params.matchId),
            }),
        },
        {
            path: `${baseUrl}/sports-cup`,
            name: RoutePage.SportsCupPage,
            component: Main,
            beforeEnter: (to, from, next) => {
                const sportType = SportType.Soccer;
                useRootStore().updateSportType(sportType);
                next();
            },
            meta: {
                isEnabled: () => {
                    const { isSportsCupZoneEnabled } = storeToRefs(useFeatureToggleStore());
                    return isSportsCupZoneEnabled.value;
                },
            },
        },
        {
            path: `${baseUrl}/e-football`,
            name: RoutePage.EFootballPage,
            component: Main,
            beforeEnter: (to, from, next) => {
                const sportType = SportType.Soccer;
                useRootStore().updateSportType(sportType);
                next();
            },
            meta: {
                isAsiOnly: true,
                isEnabled: () => {
                    const { isSwitchEFootballToSimulationSportsZoneEnabled } = storeToRefs(useFeatureToggleStore());
                    return !isSwitchEFootballToSimulationSportsZoneEnabled.value;
                },
            },
        },
        {
            path: `${baseUrl}/${multipleSportsPathPrefix}/:outright(outright)?/:sportRouteName?`,
            name: RoutePage.MultipleSportsZone,
            component: Main,
            beforeEnter: (to, from, next) => {
                if (!to.params.sportRouteName) {
                    next({
                        name: RoutePage.MultipleSportsZone,
                        params: {
                            sportRouteName: SportType.toRouteName(SportType.Soccer),
                        },
                    });
                    return;
                }

                const sportRouteName = to.params.sportRouteName;
                const sportType = typeof sportRouteName === 'string'
                    ? SportType.fromRouteName(sportRouteName)
                    : undefined;
                useRootStore().updateSportType(sportType, false);

                const outright = to.params.outright;
                useRootStore().updateMarketPage(
                    typeof outright === 'string' && outright.toLowerCase() === 'outright'
                        ? MarketPage.Outright
                        : MarketPage.All,
                );

                next();
            },
            meta: {
                isEnabled: () => {
                    const { isMultipleSportsZoneEnabled } = storeToRefs(useFeatureToggleStore());
                    return isMultipleSportsZoneEnabled.value;
                },
            },
        },
        {
            path: `${baseUrl}/cyber-sports/:sportRouteName?`,
            name: RoutePage.SimulationSportsZone,
            component: Main,
            beforeEnter: (to, from, next) => {
                const { simulationSportsZoneLeagueIds } = storeToRefs(useFeatureToggleStore());
                const { customerInfo } = storeToRefs(useAccountStore());

                const orderedSupportedSports = getOrderedSimulationSportList(
                    simulationSportsZoneLeagueIds.value,
                    customerInfo.value.currency,
                );

                if (!to.params.sportRouteName && orderedSupportedSports.length > 0) {
                    next({
                        name: RoutePage.SimulationSportsZone,
                        params: { sportRouteName: SportType.toRouteName(orderedSupportedSports[0]) },
                    });
                    return;
                }

                const sportRouteName = to.params.sportRouteName;
                const sportType = typeof sportRouteName === 'string'
                    ? SportType.fromRouteName(sportRouteName)
                    : undefined;
                useRootStore().updateSportType(sportType, false);

                next();
            },
            meta: {
                isAsiOnly: true,
                isEnabled: () => {
                    const { isSwitchEFootballToSimulationSportsZoneEnabled } = storeToRefs(useFeatureToggleStore());
                    const { customerInfo } = storeToRefs(useAccountStore());
                    return isSwitchEFootballToSimulationSportsZoneEnabled.value && customerInfo.value.isEFootballEnabled;
                },
            },
        },
        {
            path: `${baseUrl}/:sportRouteName`,
            name: RoutePage.MainBySport,
            component: Main,
            beforeEnter: (to, from, next) => {
                const sportRouteName = to.params.sportRouteName;
                const sportType = typeof sportRouteName === 'string'
                    ? SportType.fromRouteName(sportRouteName)
                    : undefined;
                if (sportType) {
                    useRootStore().updateSportType(sportType);
                    next();
                    return;
                }
                next({ name: RoutePage.NotFound });
            },
        },
        {
            path: `${baseUrl}/`,
            name: RoutePage.Main,
            redirect: () => {
                const { sportType } = storeToRefs(useRootStore());
                const sportRouteName = SportType.toRouteName(sportType.value);
                return {
                    name: RoutePage.MainBySport,
                    params: {
                        sportRouteName,
                    },
                };
            },
        },
        {
            path: '/:pathMatch(.*)*',
            name: RoutePage.NotFound,
            component: ErrorPage,
        },
    ],
    scrollBehavior,
});

function reportNavigationError(methodName: 'push' | 'replace', to: RouteLocationRaw, err: unknown) {
    if (isNavigationFailure(err, NavigationFailureType.duplicated | NavigationFailureType.cancelled)) {
        return;
    }
    const message = err instanceof Error ? err.message : String(err);
    const enriched = new Error(`router.${methodName} failed: ${message}, to: ${JSON.stringify(to)}`);
    apm.captureError(enriched);
}

function wrapNavigationMethod(methodName: 'push' | 'replace') {
    const original = router[methodName].bind(router);
    router[methodName] = ((to: RouteLocationRaw) => Promise.resolve()
        .then(() => original(to))
        .then((result) => {
            if (isNavigationFailure(result)) {
                reportNavigationError(methodName, to, result);
            }
            return result;
        })
        .catch((err: unknown) => {
            reportNavigationError(methodName, to, err);
            return err;
        })) as typeof router[typeof methodName];
}

wrapNavigationMethod('push');
wrapNavigationMethod('replace');

router.beforeEach(async (to, from, next) => {
    const isAsiOnly = typeof to.meta?.isAsiOnly === 'function'
        ? to.meta?.isAsiOnly()
        : to.meta?.isAsiOnly;

    if (!__ENV_ASI__ && isAsiOnly) {
        next({ name: RoutePage.Main });
        return;
    }

    if (!isRouteEnabled(to)) {
        next({ name: RoutePage.Main });
        return;
    }

    setLangFromCookie();
    setStateToHeaderDisplay();

    const sportType = Cookies.get('sportType');
    if (sportType) {
        Cookies.remove('sportType', { path: '/', domain: getHost() });
        next({
            name: RoutePage.MainBySport,
            params: { sportRouteName: sportType },
        });
        return;
    }

    const option = getRedirectQueryRouteOption(to);
    if (option) {
        next(option);
    } else {
        next();
    }
});

router.afterEach((to) => {
    useHistoryStore().record(to);
    recordRecentRoute(to.fullPath);
});

function normalizeParam(val: unknown): string {
    if (Array.isArray(val)) {
        return val[0] === undefined ? '' : String(val[0]);
    }
    if (val === undefined || val === null) {
        return '';
    }
    switch (typeof val) {
        case 'string':
            return val;
        case 'number':
        case 'boolean':
        case 'bigint':
        case 'symbol':
            return String(val);
        case 'object':
            return JSON.stringify(val);
        default:
            return '';
    }
}

function normalizeObject(obj: Record<string, unknown>) {
    return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k, normalizeParam(v)]),
    );
}

function getRedirectQueryRouteOption(to: RouteLocationNormalized) {
    const {
        query: {
            // eslint-disable-next-line camelcase
            redirect, promotionCode, btag, clickid, click_id, transaction_id, eventId, ...query
        },
        params,
    } = to;

    if (btag) {
        const thirtyDays = new Date(new Date().getTime() + (30 * 24 * 60 * 60 * 1000));
        Cookies.set('btag', normalizeParam(btag), { domain: getHost(), expires: thirtyDays });
    }

    // eslint-disable-next-line camelcase
    const clickId = clickid || click_id || transaction_id;
    if (clickId) {
        const thirtyDays = new Date(new Date().getTime() + (30 * 24 * 60 * 60 * 1000));
        Cookies.set('clickId', normalizeParam(clickId), { domain: getHost(), expires: thirtyDays });
    }

    if (promotionCode) {
        const inHalfADay = 0.5;
        Cookies.set('promotionCode', normalizeParam(promotionCode), { domain: getHost(), expires: inHalfADay });
    }

    if (eventId) {
        return {
            name: RoutePage.MatchDetail,
            query: normalizeObject(query),
            params: { matchId: normalizeParam(eventId) },
        };
    }

    if (redirect) {
        return {
            path: normalizeParam(redirect) ?? '/',
            query: normalizeObject(query),
            params: normalizeObject(params),
        };
    }
    return null;
}

function setStateToHeaderDisplay() {
    useHeaderDisplayStore().setDisplayMode(DisplayMode.ShowAll);
}

function isRouteEnabled(to: RouteLocationNormalized) {
    if (to.meta.isEnabled === undefined) {
        return true;
    }

    return typeof to.meta.isEnabled === 'function'
        ? to.meta.isEnabled()
        : to.meta.isEnabled;
}

export const emptyRouter = createRouter({
    history: createWebHistory(),
    routes: [{
        path: '/:pathMatch(.*)*',
        name: RoutePage.NotFound,
        component: { template: '<div></div>' },
        beforeEnter: async (to, from, next) => {
            setLangFromCookie();
            setStateToHeaderDisplay();
            next();
        },
    }],
    scrollBehavior,
});
